import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

/** sessionStorage key: the deployed build this tab already reloaded for. */
const RELOADED_FOR_KEY = "cana:auto-reloaded-for-build";

/** How soon after the tab comes back the version check must answer for the
 *  reload to still count as "on return" rather than in the middle of work. */
const RETURN_WINDOW_MS = 10_000;

/** Nothing on screen the person could lose: no open dialog, no form, no text in
 *  a field. Conservative on purpose — such a screen waits for the next screen
 *  change instead. A form counts even when its visible fields are empty: the
 *  new-request wizard keeps earlier steps in memory while a later step shows
 *  only yes/no buttons, and a reload there sends the client back to step 1. */
function isSafeToReload(touchedOnThisScreen: boolean): boolean {
	if (touchedOnThisScreen) return false;
	if (document.querySelector('[role="dialog"], form')) return false;
	// aria-hidden: helpers no one types into, e.g. the hidden twin a growing
	// textarea leaves in the page to measure its text.
	const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
		'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([aria-hidden="true"]), textarea:not([aria-hidden="true"])',
	);
	return ![...fields].some((field) => field.value.trim() !== "");
}

/**
 * Moves a stale tab onto the deployed build by itself, at a moment that cannot
 * cost anyone their work:
 *  - on the next screen change — they are leaving this screen anyway, and the
 *    reload lands them on the screen they asked for;
 *  - when they come back to the tab after it was in the background, if nothing
 *    is half-filled or open.
 *
 * Why: a tab runs the code it loaded when it opened, for as long as it stays
 * open. A doctor who kept one open for days went on saving every file twice
 * after the fix had shipped (prod, 2026-10-22 → 24), and only admins were ever
 * told a new version existed.
 *
 * At most one automatic reload per deployed build: if the reload still comes
 * back on old code (a cached page), it does not try again — no reload loop.
 */
export function useAutoReloadOnNewVersion(updateAvailable: boolean, deployedBuildId: string | null) {
	const [location] = useLocation();
	const previousLocation = useRef(location);
	const becameVisibleAt = useRef(0);
	// Any field changed since this screen opened — a filter, a select, a
	// yes/no — whether or not it sits inside a form. Edits inside a dialog do
	// not count: an open dialog already blocks the reload, and once it closes
	// what was in it has been saved or dropped.
	const touchedOnThisScreen = useRef(false);

	useEffect(() => {
		const onEdit = (event: Event) => {
			if (event.target instanceof Element && event.target.closest('[role="dialog"]')) return;
			touchedOnThisScreen.current = true;
		};
		document.addEventListener("input", onEdit, true);
		document.addEventListener("change", onEdit, true);
		return () => {
			document.removeEventListener("input", onEdit, true);
			document.removeEventListener("change", onEdit, true);
		};
	}, []);

	const reload = () => {
		if (!deployedBuildId) return;
		try {
			if (sessionStorage.getItem(RELOADED_FOR_KEY) === deployedBuildId) return;
			sessionStorage.setItem(RELOADED_FOR_KEY, deployedBuildId);
		} catch {
			// Storage unavailable (private mode): reload anyway, once per page life.
		}
		window.location.reload();
	};

	// On the next screen change.
	useEffect(() => {
		if (previousLocation.current === location) return;
		previousLocation.current = location;
		touchedOnThisScreen.current = false;
		if (updateAvailable) reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	// Back to the tab, with the update already known.
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState !== "visible") return;
			becameVisibleAt.current = Date.now();
			if (updateAvailable && isSafeToReload(touchedOnThisScreen.current)) reload();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateAvailable, deployedBuildId]);

	// Back to the tab, and the check run on return is what found the update.
	useEffect(() => {
		if (!updateAvailable) return;
		if (Date.now() - becameVisibleAt.current < RETURN_WINDOW_MS && isSafeToReload(touchedOnThisScreen.current)) reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateAvailable]);
}
